import React, { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import debounce from "lodash/debounce";
import type { ExtraType, PackType } from "../../../../../../../../common-deprecated/types/CommonTypes";
import Price from "../../../common/price/Price";
import SidePanel from "../../../../../../shared-components/css-modules/toyota/organisms/SidePanel";
import Text from "../../../../../../shared-components/css-modules/toyota/atoms/Text";
import Button from "../../../../../../shared-components/css-modules/toyota/atoms/Button";
import AddToBasket from "../../../common/AddToBasket";
import Icon from "../../../../../../shared-components/css-modules/toyota/atoms/Icon";
import cardStyles from "../../../../../../shared-components/css-modules/toyota/molecules/Card.module.scss";
import styles from "./Pack.module.scss";
import a11y from "../../../../../../shared-components/css-modules/toyota/utils/Accessibility.module.scss";
import useAphPack from "../../../../../hooks/useAphPack";
import {
    trackOptionsBasketPackAdd,
    trackOptionsBasketPackRemove,
    trackOptionsPackBrowse,
    trackOptionsPackLearnMore,
} from "../../../../../utils/tracking";
import { useCarConfigSelector } from "../../../../../redux/store";
import { showAccessoriesRate } from "../../../../../utils/financeInsuranceRate";
import Collapse from "../../../../../../shared-components/css-modules/toyota/molecules/Collapse";
import { getCarDBAssetSrcSet } from "../../../../../../../shared-logic/utils/imageUtils";
import Popover from "../../../../../../shared-components/css-modules/toyota/vendor/popover/Popover";
import Incentive from "../../../../../../shared-components/css-modules/toyota/molecules/Incentive";
import { scrollToTarget } from "../../../../../../../../common-deprecated/utils/scrollUtils";
import { CarConfigConfigureSection } from "../../../../../utils/types/SectionTypes";
import { CarConfigLabel, useCarConfigLabel } from "../../../../../utils/labelUtils";
import SanitizedRenderer from "../../../../../../../../common-deprecated/components/SanitizedRenderer";

const Pack: React.FC<{ pack: PackType; firstShownPack?: boolean }> = ({ pack, firstShownPack }) => {
    const [isSidePanelOpen, setIsSidePanelOpen] = useState<boolean>(false);
    const [nextButtonDisabled, setNextButtonDisabled] = useState<boolean>(false);
    const [prevButtonDisabled, setPrevButtonDisabled] = useState<boolean>(false);
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const carConfigSettings = useCarConfigSelector((state) => state.carConfigSettings);
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    const ariaPacksInfo = useCarConfigLabel("carConfigA11yButtonPacksInfo");
    const packProps = useAphPack(pack);

    const scrollFunction = useCallback(
        (direction: 1 | -1 = 1) =>
            (event: React.MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                if (wrapperRef.current) {
                    const anyItemWidth = wrapperRef.current.children[0]?.clientWidth || 0;
                    wrapperRef.current.scrollTo({
                        left: wrapperRef.current.scrollLeft + anyItemWidth * direction,
                        behavior: "smooth",
                    });
                }
            },
        [wrapperRef],
    );

    const setButtonStates = (): void => {
        if (wrapperRef.current) {
            const target = wrapperRef.current;
            setPrevButtonDisabled(target.scrollLeft === 0);
            setNextButtonDisabled(target.scrollWidth - target.offsetWidth - target.scrollLeft < 1);
        }
    };

    useEffect(() => {
        setButtonStates();
    }, []);

    const trackBrowseDebounced = useCallback(
        debounce(() => {
            trackOptionsPackBrowse();
        }, 300),
        [],
    );

    if (!packProps) return null;

    const { actions, packContent } = packProps;
    const { selectPack, deselectPack, selectPackExtra, deselectPackExtra, displayNotifications } = actions;
    const {
        selected,
        totalPackContent,
        colours,
        wheels,
        upholsteries,
        renderedEquipment,
        optionalEquipment,
        selectedEquipment,
        availability,
    } = packContent;

    const handleClickPack = (): void => {
        if (availability.enabled) {
            if (active) {
                trackOptionsBasketPackRemove();
                deselectPack();
            } else {
                trackOptionsBasketPackAdd();
                selectPack();
                displayNotifications();
            }
        }
    };

    const handleClickPackExtra = (
        event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
        extra: ExtraType,
    ): void => {
        event.stopPropagation();
        if (selectedEquipment.some((id) => id === extra.id)) {
            deselectPackExtra(extra);
        } else {
            selectPackExtra(extra);
        }
    };

    /* styles get toggled based on this const */
    const active = selected;

    return (
        <>
            <article
                className={clsx(
                    cardStyles.card,
                    availability.enabled && cardStyles.clickable,
                    availability.reason && cardStyles["not-available"],
                    styles.wrapper,
                    active && cardStyles.active,
                )}
                onClick={handleClickPack}
                data-testid="packs-wrapper"
            >
                {pack.subtitle && (
                    <Incentive label={pack.subtitle} variant="best-seller" className={styles.highlighted} />
                )}
                {availability.reason && (
                    <div className={styles["not-available-header"]}>
                        <CarConfigLabel
                            label={
                                availability.reason === "colour"
                                    ? "carConfigPackUnavailableForCurrentColour"
                                    : "carConfigPackUnavailableForCurrentUpholstery"
                            }
                        />

                        {availability.reason && (
                            <Button
                                className={styles["not-available-button"]}
                                variant="btn-text"
                                onClick={() =>
                                    scrollToTarget(
                                        availability.reason === "colour"
                                            ? CarConfigConfigureSection.Colour
                                            : CarConfigConfigureSection.Upholstery,
                                    )
                                }
                            >
                                <CarConfigLabel
                                    label={
                                        availability.reason === "colour"
                                            ? "carConfigViewColours"
                                            : "carConfigViewUpholsteries"
                                    }
                                />
                            </Button>
                        )}
                    </div>
                )}

                <header className={styles.header}>
                    <div className={styles["title-wrapper"]}>
                        <Text tag="h3" variant="apheleia-h4">
                            {pack.name}
                        </Text>
                        {!!pack.description && (
                            <Popover placement="bottom">
                                <Popover.Trigger>
                                    <Button
                                        variant="clean"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                        }}
                                        aria-label={ariaPacksInfo}
                                    >
                                        <Icon variant="info" sizing="md" aria-hidden="true" />
                                        <span className={a11y["sr-only"]}>
                                            <CarConfigLabel label="carConfigA11yButtonPacksInfo" />
                                        </span>
                                    </Button>
                                </Popover.Trigger>
                                <Popover.Content
                                    onClick={(event) => {
                                        event.stopPropagation();
                                    }}
                                >
                                    <SanitizedRenderer html={pack.description} />
                                    <Popover.Close>
                                        <Button
                                            variant="apheleia-text"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                            }}
                                            className={styles.link}
                                        >
                                            <CarConfigLabel label="carConfigTooltipClose" />
                                        </Button>
                                    </Popover.Close>
                                </Popover.Content>
                            </Popover>
                        )}
                    </div>
                    <Text variant="apheleia-h5" className={styles.amount}>
                        <CarConfigLabel label="carConfigPacksExtaCount" params={{ count: totalPackContent }} />
                    </Text>
                </header>
                <div className={styles.scroller}>
                    <nav className={styles["scroller-nav"]}>
                        <Button
                            circle
                            variant="btn-secondary"
                            className={styles["scroller-nav-item"]}
                            onClick={scrollFunction(-1)}
                            disabled={prevButtonDisabled}
                            style={{
                                display: prevButtonDisabled ? "none" : undefined,
                            }}
                        >
                            <Icon variant="chevron-left" sizing="md" aria-hidden="true" />
                            <span className={a11y["sr-only"]}>
                                <CarConfigLabel label="carConfigA11yButtonSlidePrevious" />
                            </span>
                        </Button>
                        <Button
                            circle
                            variant="btn-secondary"
                            className={styles["scroller-nav-item"]}
                            onClick={scrollFunction(1)}
                            disabled={nextButtonDisabled}
                            style={{
                                display: nextButtonDisabled ? "none" : undefined,
                            }}
                        >
                            <Icon variant="chevron-right" sizing="md" aria-hidden="true" />
                            <span className={a11y["sr-only"]}>
                                <CarConfigLabel label="carConfigA11yButtonSlideNext" />
                            </span>
                        </Button>
                    </nav>
                    <div
                        className={styles.items}
                        ref={wrapperRef}
                        onScroll={() => {
                            trackBrowseDebounced();
                            setButtonStates();
                        }}
                    >
                        {colours.length > 0 &&
                            colours.map((colour) => (
                                <div key={colour.id} className={styles.extra}>
                                    <img
                                        {...getCarDBAssetSrcSet(
                                            commonSettings,
                                            [
                                                { width: 128, height: 128 },
                                                { width: 254, height: 254 },
                                            ],
                                            colour.image,
                                        )}
                                        width="128"
                                        height="128"
                                        sizes="128px"
                                        alt={colour.name}
                                        loading="lazy"
                                    />
                                    <Text tag="h4" variant="apheleia-body" color="grey-7" className={styles.info}>
                                        {colour.name}
                                    </Text>
                                </div>
                            ))}
                        {wheels.length > 1 &&
                            wheels.map((wheel) => (
                                <div key={wheel.id} className={styles.extra}>
                                    <img
                                        {...getCarDBAssetSrcSet(
                                            commonSettings,
                                            [
                                                { width: 128, height: 128 },
                                                { width: 254, height: 254 },
                                            ],
                                            wheel.image,
                                        )}
                                        width="128"
                                        height="128"
                                        sizes="128px"
                                        alt={wheel.name}
                                        loading="lazy"
                                    />
                                    <Text tag="h4" variant="apheleia-body" color="grey-7" className={styles.info}>
                                        {wheel.name}
                                    </Text>
                                </div>
                            ))}

                        {upholsteries.length > 1 &&
                            upholsteries.map((upholstery) => (
                                <div key={upholstery.id} className={styles.extra}>
                                    <img
                                        {...getCarDBAssetSrcSet(
                                            commonSettings,
                                            [
                                                { width: 128, height: 128 },
                                                { width: 254, height: 254 },
                                            ],
                                            upholstery.image,
                                        )}
                                        width="128"
                                        height="128"
                                        sizes="128px"
                                        alt={upholstery.name}
                                        loading="lazy"
                                    />
                                    <Text tag="h4" variant="apheleia-body" color="grey-7" className={styles.info}>
                                        {upholstery.name}
                                    </Text>
                                </div>
                            ))}

                        {renderedEquipment.map((item) => (
                            <div key={item.id} className={styles.extra}>
                                <img
                                    {...getCarDBAssetSrcSet(
                                        commonSettings,
                                        [
                                            { width: 128, height: 128 },
                                            { width: 254, height: 254 },
                                        ],
                                        item.image,
                                    )}
                                    width="128"
                                    height="128"
                                    sizes="128px"
                                    alt={item.name}
                                    loading="lazy"
                                />
                                <Text tag="h4" variant="apheleia-body" color="grey-7" className={styles.info}>
                                    {item.name}
                                </Text>
                            </div>
                        ))}
                        {optionalEquipment.length > 0 &&
                            optionalEquipment.map((equipment) => (
                                <div
                                    key={equipment.id}
                                    className={clsx(
                                        styles.extra,
                                        styles.equipment,
                                        active && styles.clickable,
                                        selectedEquipment.some((id) => id === equipment.id) && styles.active,
                                    )}
                                >
                                    <img
                                        {...getCarDBAssetSrcSet(
                                            commonSettings,
                                            [
                                                { width: 128, height: 128 },
                                                { width: 254, height: 254 },
                                            ],
                                            equipment.image,
                                        )}
                                        width="128"
                                        height="128"
                                        sizes="128px"
                                        alt={equipment.name}
                                        loading="lazy"
                                    />
                                    <Text tag="h4" variant="apheleia-body" color="grey-7" className={styles.info}>
                                        {equipment.name}
                                    </Text>
                                    <footer className={styles["equipment-footer"]}>
                                        <div className={styles.price}>
                                            <Text variant="apheleia-price">
                                                <Price
                                                    price={equipment.price}
                                                    forceCashPrice={
                                                        !showAccessoriesRate(commonSettings, carConfigSettings)
                                                    }
                                                />
                                            </Text>
                                            <AddToBasket
                                                itemName={equipment.name}
                                                active={selectedEquipment.some((id) => id === equipment.id)}
                                                className={styles.button}
                                                onClick={(event) => handleClickPackExtra(event, equipment)}
                                            />
                                        </div>
                                    </footer>
                                </div>
                            ))}
                    </div>
                </div>

                <footer className={styles.footer}>
                    <Button
                        variant="apheleia-text"
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            trackOptionsPackLearnMore();
                            setIsSidePanelOpen(true);
                        }}
                        autoFocus={firstShownPack}
                    >
                        <CarConfigLabel label="carConfigPacksLearnMore" />
                    </Button>
                    {!pack.standard && (
                        <div className={styles.price}>
                            <Text variant="apheleia-price">
                                <Price
                                    price={pack.price}
                                    forceCashPrice={!showAccessoriesRate(commonSettings, carConfigSettings)}
                                />
                            </Text>
                            {availability.enabled && (
                                // No onClick necessary, as the entire card is clickable, otherwise this could trigger 2 notifs at the same time
                                <AddToBasket
                                    itemName={pack.name}
                                    active={active}
                                    className={clsx(cardStyles.indicator, styles.button)}
                                    aria-pressed={selected}
                                    data-testid="pack-add-to-basket-button"
                                />
                            )}
                        </div>
                    )}

                    {pack.standard && (
                        <Text variant="apheleia-price" className={styles.included}>
                            <CarConfigLabel label="carConfigIncluded" />
                        </Text>
                    )}
                </footer>
            </article>
            <SidePanel
                open={isSidePanelOpen}
                onRequestClose={() => {
                    setIsSidePanelOpen(false);
                }}
            >
                <header className={styles["panel-header"]}>
                    <CarConfigLabel
                        label="carConfigPacksSidepanelTitle"
                        params={{ title: pack.name, count: totalPackContent }}
                        useHTML
                    />
                </header>
                <div className={styles["panel-content"]}>
                    {renderedEquipment.map((item) => (
                        <div key={item.id} className={styles["panel-item"]}>
                            <Collapse summary={item.name} className={styles.details}>
                                <figure className={styles["panel-item-image"]}>
                                    <img
                                        {...getCarDBAssetSrcSet(
                                            commonSettings,
                                            [
                                                { width: 380, height: 380 },
                                                { width: 450, height: 450 },
                                                { width: 504, height: 504 },
                                                { width: 800, height: 800 },
                                                { width: 1000, height: 1000 },
                                            ],
                                            item.image,
                                        )}
                                        width="380"
                                        height="380"
                                        sizes="(max-width: 1199px) 100vw, 50vw"
                                        alt={item.name}
                                    />
                                </figure>
                                {item.description && (
                                    <Text variant="apheleia-body" spacing="spacing-0">
                                        <SanitizedRenderer html={item.description} />
                                    </Text>
                                )}
                            </Collapse>
                        </div>
                    ))}
                </div>
            </SidePanel>
        </>
    );
};

export default Pack;
